// Sales orders — data access (docs/design/sales.md, ADR-026).
//
//   list     rpc sales_order_page(company, status, term, after_date, after_number, limit 51)
//            keyset on (order_date desc, number desc); indexes sales_orders_open_idx /
//            _status_idx / _list_idx; search: number prefix (sales_orders_number_key)
//            or customer trigram. One request per page, ≈ 210 B per row (short-line count
//            kept on the header, no line scan).
//   detail   rpc sales_order_document(id) — header, ship-to snapshot, lines with
//            reservation and warehouse availability, one round-trip (50 lines ≈ 40 KB).
//   writes   rpc save_sales_order / release_ / reserve_ / cancel_ / close_sales_order_line /
//            close_sales_order. Tables are read-only for API users; the database
//            validates, reserves and writes the audit log. save sends expected_revision.
// Money and quantities travel as decimal strings (Postgres numeric), never floats.

import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import type { Page } from "@/components/DataList";
import { isDemoMode, supabase } from "@/lib/supabase";
import { demoSoApi } from "./demo";
import { sortParam, type ListFilter, type ListSort } from "@patronix/domain/list";

export const salesOrderStatuses = ["draft", "released", "partially_shipped", "shipped", "closed", "cancelled"] as const;
export type SalesOrderStatus = (typeof salesOrderStatuses)[number];

export type StatusFilter = "open" | "all" | SalesOrderStatus;

export function isStatusFilter(v: unknown): v is StatusFilter {
  return v === "open" || v === "all" || (salesOrderStatuses as readonly unknown[]).includes(v);
}

export interface SalesOrderRow {
  id: string;
  number: string;
  status: SalesOrderStatus;
  customer_id: string;
  customer_code: string;
  customer_name: string;
  order_date: string;
  next_requested_date: string | null;
  total_amount: string | number;
  currency: string;
  line_count: number;
  /** Open lines not fully reserved (released orders; 0 otherwise). */
  short_line_count: number;
}

export interface SalesOrderLine {
  id: string;
  line_no: number;
  item_id: string;
  item_code: string;
  item_description: string;
  lot_controlled: boolean;
  ordered_qty: string | number;
  uom_id: string;
  uom_code: string;
  uom_decimals: number;
  ordered_base_qty: string | number;
  reserved_base_qty: string | number;
  shipped_base_qty: string | number;
  base_uom_code: string;
  base_uom_decimals: number;
  unit_price: string | number | null;
  /** Gross price the discount applies to (ADR-052); unit_price is the net. */
  list_price?: string | number | null;
  discount_pct?: string | number;
  line_amount: string | number | null;
  requested_date: string | null;
  warehouse_id: string;
  warehouse_code: string;
  /** Reservable on-hand (active, pickable, unblocked) − all reservations in the line's warehouse (may be negative). */
  available_base_qty: string | number;
  status: "open" | "shipped" | "closed";
  /** Project peg (PR 2); the line reserves and ships that project's stock (PR 3b). */
  project_id?: string | null;
  project_code?: string | null;
}

export interface ShipTo {
  address_id: string;
  name: string | null;
  line1: string;
  line2: string | null;
  postal_code: string | null;
  city: string;
  country: string;
}

export interface SalesOrderDocument {
  id: string;
  number: string;
  status: SalesOrderStatus;
  company_id: string;
  company_code: string;
  customer_id: string;
  customer_code: string;
  customer_name: string;
  order_date: string;
  currency: string;
  customer_reference: string | null;
  payment_terms: string | null;
  ship_to: ShipTo | null;
  note: string | null;
  warehouse_id: string | null;
  warehouse_code: string | null;
  next_requested_date: string | null;
  total_amount: string | number;
  short_line_count: number;
  revision: number;
  line_count: number;
  released_at: string | null;
  closed_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
  created_by_name: string | null;
  lines: SalesOrderLine[];
}

export interface SalesOrderHeaderInput {
  order_date?: string;
  currency?: string;
  warehouse_id?: string | null;
  customer_reference?: string | null;
  payment_terms?: string | null;
  note?: string | null;
  ship_to_address_id?: string | null;
  expected_revision?: number;
}

export interface SalesOrderLineInput {
  line_id?: string;
  item_id: string;
  qty: string;
  /** Leave out on a new line to take the item's sales unit (ADR-047). */
  uom_id?: string;
  /** Discount % (ADR-052); leave out on a new line to take the customer's rule. */
  discount_pct?: string;
  /** Leave out on a new line to take the price list / last / list price. */
  unit_price?: string | null;
  requested_date?: string | null;
  warehouse_id?: string | null;
}

export interface SaveResult {
  id: string;
  number: string;
  status: SalesOrderStatus;
  revision: number;
  /** Open quantity that could not be reserved (released orders). */
  short_qty: string | number;
}

export interface ListParams {
  companyId: string;
  status: StatusFilter;
  q: string;
  /** Column filters and sort (list RPC spec in @patronix/domain/list). */
  filters?: ListFilter[];
  sort?: ListSort | null;
}

export const SO_PAGE = 50;

function db() {
  if (!supabase) throw new Error("Supabase is not configured");
  return supabase;
}

export async function listSalesOrders(p: ListParams, cursor: string | null, signal: AbortSignal, size = SO_PAGE): Promise<Page<SalesOrderRow>> {
  if (isDemoMode) return demoSoApi.list(p, cursor);
  const { data, error } = await db()
    .rpc("sales_order_page", {
      p_company_id: p.companyId,
      p_status: p.status,
      p_term: p.q.trim() || null,
      p_filters: p.filters ?? [],
      p_sort: sortParam(p.sort ?? null),
      p_after: cursor ? JSON.parse(cursor) : null,
      p_limit: size + 1,
    })
    .abortSignal(signal);
  if (error) throw error;
  const rows = data as (SalesOrderRow & { sort_cursor: unknown })[];
  const hasMore = rows.length > size;
  const page = hasMore ? rows.slice(0, size) : rows;
  const last = page[page.length - 1];
  return { rows: page, nextCursor: hasMore && last ? JSON.stringify(last.sort_cursor) : null };
}

async function getSalesOrder(id: string, signal: AbortSignal): Promise<SalesOrderDocument | null> {
  if (isDemoMode) return demoSoApi.get(id);
  const { data, error } = await db().rpc("sales_order_document", { p_id: id }).abortSignal(signal);
  if (error) throw error;
  return (data as SalesOrderDocument | null) ?? null;
}

export async function saveSalesOrder(
  id: string | null,
  companyId: string,
  customerId: string,
  header: SalesOrderHeaderInput,
  lines: SalesOrderLineInput[],
): Promise<SaveResult> {
  if (isDemoMode) return demoSoApi.save(id, companyId, customerId, header, lines);
  const { data, error } = await db().rpc("save_sales_order", {
    p_id: id,
    p_company_id: companyId,
    p_customer_id: customerId,
    p_header: header,
    p_lines: lines,
  });
  if (error) throw error;
  return data as SaveResult;
}

export type Transition = "release" | "reserve" | "cancel" | "close";

export async function transitionSalesOrder(id: string, action: Transition): Promise<{ short_qty?: string | number }> {
  if (isDemoMode) return demoSoApi.transition(id, action);
  const fn = {
    release: "release_sales_order",
    reserve: "reserve_sales_order",
    cancel: "cancel_sales_order",
    close: "close_sales_order",
  }[action];
  const { data, error } = await db().rpc(fn, { p_id: id });
  if (error) throw error;
  return (data as { short_qty?: string | number } | null) ?? {};
}

export async function closeSalesOrderLine(id: string, lineId: string): Promise<void> {
  if (isDemoMode) return demoSoApi.closeLine(id, lineId);
  const { error } = await db().rpc("close_sales_order_line", { p_id: id, p_line_id: lineId });
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Shipments (post_shipment / reverse_shipment / shipment_proposal)
// ---------------------------------------------------------------------------
//   proposal   rpc shipment_proposal(order) — FEFO picks per open line, one round-trip
//              (balances by inventory_balances_key); loaded when the ship form opens.
//   shipments  rpc sales_order_shipments(order) — 50 newest with lines (shipments_order_idx).
//   post       one RPC: engine call, own reservation consumed first, SO status, number.

export interface ShipmentPick {
  location_id: string;
  location_code: string;
  lot_id: string | null;
  lot_code: string | null;
  expiry_date: string | null;
  on_hand: string | number;
  qty: string | number;
  /** Serial items: the proposed units of this pick. */
  serials?: string[];
}

export interface ProposalLine {
  order_line_id: string;
  line_no: number;
  item_id: string;
  item_code: string;
  lot_controlled: boolean;
  serial_controlled?: boolean;
  base_uom_code: string;
  base_uom_decimals: number;
  warehouse_code: string;
  open_base_qty: string | number;
  reserved_base_qty: string | number;
  picks: ShipmentPick[];
}

export interface ShipmentLineInput {
  order_line_id: string;
  qty: string;
  location_id?: string;
  lot_id?: string;
  serials?: string[];
}

export interface Shipment {
  id: string;
  number: string;
  status: "draft" | "posted" | "cancelled";
  delivery_note: string | null;
  note: string | null;
  reverses_shipment_id: string | null;
  reversed_by_id: string | null;
  posted_at: string;
  posted_by_name: string | null;
  lines: {
    line_no: number;
    order_line_id: string;
    item_code: string;
    qty: string | number;
    uom_code: string;
    base_qty: string | number | null;
    location_code: string | null;
    lot_code: string | null;
    /** The first 10 serials of the line; serial_count tells how many there are. */
    serial_numbers?: string[] | null;
    serial_count?: number | null;
  }[];
}

async function getProposal(orderId: string, signal: AbortSignal): Promise<ProposalLine[]> {
  if (isDemoMode) return [];
  const { data, error } = await db().rpc("shipment_proposal", { p_order_id: orderId }).abortSignal(signal);
  if (error) throw error;
  return ((data as { lines?: ProposalLine[] } | null)?.lines ?? []) as ProposalLine[];
}

async function listShipments(orderId: string, signal: AbortSignal): Promise<{ shipments: Shipment[]; has_more: boolean }> {
  if (isDemoMode) return { shipments: [], has_more: false };
  const { data, error } = await db().rpc("sales_order_shipments", { p_order_id: orderId }).abortSignal(signal);
  if (error) throw error;
  return (data as { shipments: Shipment[]; has_more: boolean } | null) ?? { shipments: [], has_more: false };
}

export async function postShipment(orderId: string, deliveryNote: string | null, lines: ShipmentLineInput[]) {
  if (isDemoMode) throw new Error("Shipments are not available in demo mode");
  const { data, error } = await db().rpc("post_shipment", { p_order_id: orderId, p_delivery_note: deliveryNote, p_lines: lines });
  if (error) throw error;
  return data as { id: string; number: string; order_status: SalesOrderStatus };
}

export async function reverseShipment(id: string): Promise<void> {
  if (isDemoMode) throw new Error("Shipments are not available in demo mode");
  const { error } = await db().rpc("reverse_shipment", { p_id: id });
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Query options (loaders, hover prefetch and components share them)
// ---------------------------------------------------------------------------

export const salesOrderKeys = {
  all: ["sales", "sales-orders"] as const,
  list: (p: ListParams) => [...salesOrderKeys.all, "list", p] as const,
  detail: (id: string) => [...salesOrderKeys.all, "detail", id] as const,
  proposal: (id: string) => [...salesOrderKeys.all, "proposal", id] as const,
  shipments: (id: string) => [...salesOrderKeys.all, "shipments", id] as const,
};

// Picks depend on live stock: always fresh when the form opens.
export function shipmentProposalQuery(id: string) {
  return queryOptions({ queryKey: salesOrderKeys.proposal(id), queryFn: ({ signal }) => getProposal(id, signal), staleTime: 0 });
}

export function salesOrderShipmentsQuery(id: string) {
  return queryOptions({ queryKey: salesOrderKeys.shipments(id), queryFn: ({ signal }) => listShipments(id, signal), staleTime: 30_000 });
}

export function salesOrderListQuery(params: ListParams) {
  return infiniteQueryOptions({
    queryKey: salesOrderKeys.list(params),
    queryFn: ({ pageParam, signal }) => listSalesOrders(params, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: 30_000,
    enabled: !!params.companyId,
  });
}

// Availability moves with every posting elsewhere: shorter staleTime than POs.
export function salesOrderDetailQuery(id: string) {
  return queryOptions({
    queryKey: salesOrderKeys.detail(id),
    queryFn: ({ signal }) => getSalesOrder(id, signal),
    staleTime: 15_000,
  });
}

export const statusTone = {
  draft: "neutral",
  released: "info",
  partially_shipped: "warning",
  shipped: "success",
  closed: "neutral",
  cancelled: "danger",
} as const satisfies Record<SalesOrderStatus, string>;

export const isOpenStatus = (s: SalesOrderStatus) => s === "draft" || s === "released" || s === "partially_shipped";
export const holdsReservations = (s: SalesOrderStatus) => s === "released" || s === "partially_shipped";

/** Translated message for known database rules; anything else as sent. */
const rules: [RegExp, string][] = [
  [/was changed by someone else/, "so.err.stale"],
  [/^Customer .* is inactive/, "so.err.customerInactive"],
  [/^The customer is inactive/, "so.err.customerInactive"],
  [/^Customer not found/, "so.err.customerNotFound"],
  [/^The customer cannot change after release/, "so.err.customerLocked"],
  [/^The order date cannot change after release/, "so.err.dateLocked"],
  [/^Currency cannot change after release/, "so.err.currencyLocked"],
  [/^Currency must be a three-letter code/, "po.err.currency"],
  [/^Warehouse not found in the company or inactive/, "po.err.warehouse"],
  [/^Ship-to address not found/, "so.err.shipTo"],
  [/^A sales order needs at least one line/, "po.err.noLines"],
  [/^Goods were shipped on this order/, "so.err.shippedCannotCancel"],
  [/^Only draft or released sales orders without shipments can be cancelled/, "so.err.cannotCancel"],
  [/^Only draft sales orders can be released/, "so.err.cannotRelease"],
  [/^Missing permission/, "so.err.permission"],
  [/^This is the last open line and nothing was shipped/, "so.err.lastLine"],
  [/^Stock of .* is reserved for other orders/, "so.err.reservedForOthers"],
  [/^Goods can be shipped only on released orders/, "so.err.notShippable"],
  [/^A shipment needs at least one line/, "so.err.shipmentEmpty"],
  [/its shipments can no longer be reversed/, "so.err.reverseClosed"],
  [/is already reversed/, "po.err.alreadyReversed"],
  [/^Not enough stock/, "so.err.notEnoughStock"],
  [/cannot be removed from a released order/, "so.err.lineRemoved"],
  [/^A released sales order keeps its lines/, "so.err.lineRemoved"],
];

const lineRules: [RegExp, string][] = [
  [/^price is missing/, "po.err.priceMissing"],
  [/^price is required on a released order/, "po.err.priceMissing"],
  [/^requested date is missing/, "so.err.dateMissing"],
  [/^item is inactive/, "po.err.itemInactive"],
  [/^item and quantity are required/, "po.err.incompleteLine"],
  [/^quantity must be above zero/, "po.err.qtyPositive"],
  [/^services cannot be sold yet/, "so.err.service"],
  [/^serial numbers are required/, "serials.err.required"],
  [/^\d+ serial numbers for a quantity of/, "serials.err.count"],
  [/cannot be below what was already shipped/, "so.err.belowShipped"],
  [/cannot change once the order is released/, "so.err.releasedLocked"],
  [/^line is (closed|shipped) and cannot change/, "po.err.lineClosed"],
  [/^warehouse is required/, "so.err.warehouseRequired"],
  [/^more than ordered would be shipped/, "so.err.overShip"],
  [/^lot is required/, "documents.err.lotRequired"],
  [/^lot is blocked/, "so.err.lotBlocked"],
  [/^location does not issue stock/, "so.err.locationNoIssue"],
  [/^location is blocked/, "documents.err.locationBlocked"],
  [/^the order line is/, "po.err.orderLineClosed"],
  [/cannot be expressed in the base unit/, "documents.err.decimals"],
  [/^No unit conversion/, "documents.err.noConversion"],
];

export function soErrorMessage(t: TFunction, error: unknown): string {
  const tr = t as unknown as (key: string, o?: Record<string, string>) => string;
  const message = (error as { message?: string } | null)?.message ?? String(error ?? "");
  const line = /^Line (\d+):? (.+)$/.exec(message);
  if (line && !/cannot be removed/.test(message)) {
    const rule = lineRules.find(([re]) => re.test(line[2] ?? ""));
    return tr("po.err.line", { line: line[1] ?? "", message: rule ? tr(rule[1]) : (line[2] ?? "") });
  }
  const rule = rules.find(([re]) => re.test(message));
  return rule ? tr(rule[1]) : message || tr("auth.errorGeneric");
}
